import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

// Per-environment RUM configuration — mirrors the pattern from the STO (Ember) project.
// staging uses 100% replay to help catch and diagnose issues before production.
// production caps replay at 20% for cost and privacy reasons.
// development disables sampling entirely so local sessions are never recorded.
const RUM_ENV_CONFIG = {
	development: {
		sessionSampleRate: 0,
		sessionReplaySampleRate: 0,
		defaultPrivacyLevel: 'allow' as const,
	},
	staging: {
		sessionSampleRate: 100,
		sessionReplaySampleRate: 100,
		defaultPrivacyLevel: 'mask-user-input' as const,
	},
	production: {
		sessionSampleRate: 100,
		sessionReplaySampleRate: 20,
		defaultPrivacyLevel: 'mask-user-input' as const,
	},
} as const

type AppEnv = keyof typeof RUM_ENV_CONFIG

const appEnv: AppEnv =
	import.meta.env.VITE_APP_ENV in RUM_ENV_CONFIG ? import.meta.env.VITE_APP_ENV : 'development'

const rumEnvConfig = RUM_ENV_CONFIG[appEnv]

/** True when Datadog Logs is configured. Uses its own dedicated client token. */
export const isDatadogLogsEnabled = (): boolean =>
	Boolean(import.meta.env.VITE_DATADOG_CLIENT_TOKEN)

/**
 * True when Datadog RUM is fully configured.
 * RUM uses a separate client token from Logs — do not share VITE_DATADOG_CLIENT_TOKEN here.
 */
export const isDatadogRUMEnabled = (): boolean =>
	Boolean(
		import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN &&
		import.meta.env.VITE_DATADOG_APPLICATION_ID,
	)

export function initDatadogLogs(): void {
	if (!isDatadogLogsEnabled()) return

	datadogLogs.init({
		clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'online-ordering-frontend-v2',
		env: appEnv,
		forwardErrorsToLogs: true,
		sessionSampleRate: 100,
	})
}

export function initDatadogRUM(): void {
	if (!isDatadogRUMEnabled()) return

	datadogRum.init({
		applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
		clientToken: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'online-ordering-frontend-v2',
		env: appEnv,
		version: import.meta.env.VITE_APP_VERSION,
		sessionSampleRate: rumEnvConfig.sessionSampleRate,
		sessionReplaySampleRate: rumEnvConfig.sessionReplaySampleRate,
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		defaultPrivacyLevel: rumEnvConfig.defaultPrivacyLevel,
	})

	datadogRum.startSessionReplayRecording()
}
